/* ═══════════════════════════════════════════════════════════════
   ReportsPage — Canonical reports for PMs / billing / principals.

   Six reports out of the box:
     1. Utilization        — billable / capacity per person, period
     2. WIP                — billable hours not yet invoiced, per project
     3. Project Profitability — actual cost vs budget per project
     4. AR Aging           — current / 30 / 60 / 90+ unpaid buckets
     5. Time by Project    — hours per project, period, optional grouping
     6. Time by Person     — hours per person, period, optional grouping

   Each report has:
     - Filter rail (date range, departments, projects, people)
     - Table view with totals row
     - Chart visualization (bar / line / pie depending on data shape)
     - CSV + PDF export
     - Saved-views support (DashboardViewService surface = 'reports.<id>')

   Permissions:
     - reports.view to open the page
     - reports.run_<canonical_id> per-report
     - reports.view_financials gates $ figures
     - reports.export_csv / reports.export_pdf for downloads
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Lock, ChevronDown, Check } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { rem } from '../lib/rem';
import { Button } from '../components/ui/button';
import EmptyState from '../components/EmptyState';
import { hasPermission } from '../../shared/permissions';
import type { AuthUser } from '../services/authService';
import type {
  HourEntry,
  Project,
  Organization,
  TeamMember,
  BillingRole,
  Invoice,
} from '../../shared/types';
import type { Department, ProjectBudget } from '../../shared/entities';
import { TimeService, addDays, mondayOf } from '../services/timeService';
import { ProjectService } from '../services/projectService';
import { OrgService } from '../services/orgService';
import { TeamService } from '../services/teamService';
import { AdminService } from '../services/adminService';
import { DepartmentService } from '../services/departmentService';
import { ProjectBudgetService } from '../services/projectBudgetService';
import { InvoiceService } from '../services/invoiceService';
import { useTableSync } from '../hooks/useTableSync';

interface Props {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
}

type ReportId =
  | 'utilization'
  | 'wip'
  | 'profitability'
  | 'ar_aging'
  | 'time_by_project'
  | 'time_by_person';

interface ReportMeta {
  id: ReportId;
  label: string;
  description: string;
  permissionKey: string;
  needsFinancials?: boolean;
}

const REPORTS: ReportMeta[] = [
  {
    id: 'utilization',
    label: 'Utilization',
    description: 'Billable hours / capacity per person over a period',
    permissionKey: 'reports.run_utilization',
  },
  {
    id: 'wip',
    label: 'Work-In-Progress',
    description: 'Billable hours logged but not yet invoiced, per project',
    permissionKey: 'reports.run_wip',
    needsFinancials: true,
  },
  {
    id: 'profitability',
    label: 'Project Profitability',
    description: 'Actual cost vs budget per project',
    permissionKey: 'reports.run_profitability',
    needsFinancials: true,
  },
  {
    id: 'ar_aging',
    label: 'AR Aging',
    description: 'Outstanding receivables bucketed by age',
    permissionKey: 'reports.run_ar_aging',
    needsFinancials: true,
  },
  {
    id: 'time_by_project',
    label: 'Time by Project',
    description: 'Hours summed per project for a period, optional grouping',
    permissionKey: 'reports.run_time_by_project',
  },
  {
    id: 'time_by_person',
    label: 'Time by Person',
    description: 'Hours summed per person for a period, grouped by department',
    permissionKey: 'reports.run_time_by_person',
  },
];

const fmtMoney = (n: number) =>
  '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtHours = (n: number) =>
  (n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'h';
const fmtPct = (n: number) =>
  (n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 }) + '%';

// True when no logged hours carry into this report for the current
// range/filters. In that case the chart would be a blank frame and the
// utilization column would read an all-red "0%" — alarming noise that
// is meaningless without data. We swap in a proper empty-data state.
function hasNoHours(entries: { hours?: number | string }[]): boolean {
  if (!entries || entries.length === 0) return true;
  const total = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  return total === 0;
}

// Clock-with-dashes glyph for the time reports' empty state. Inline SVG.
const NoTimeIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5l3 2" />
  </svg>
);

const NO_TIME_TITLE = 'No time logged in this range';
const NO_TIME_SUBTITLE =
  'Once hours are logged for these dates, the chart and breakdown appear here. Try widening the date range or clearing filters.';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Page ────────────────────────────────────────────────────────────

export default function ReportsPage({ toast, authUser }: Props) {
  const perms = authUser?.permissions || [];
  const canViewMoney = hasPermission(perms, 'reports.view_financials');
  const canExportCsv = hasPermission(perms, 'reports.export_csv');
  const canExportPdf = hasPermission(perms, 'reports.export_pdf');

  const availableReports = REPORTS.filter((r) => hasPermission(perms, r.permissionKey));

  const [activeReport, setActiveReport] = useState<ReportId>(
    () => availableReports[0]?.id || 'utilization'
  );

  // Filters
  const [startDate, setStartDate] = useState(() => addDays(todayIso(), -30));
  const [endDate, setEndDate] = useState(() => todayIso());
  const [selectedDeptIds, setSelectedDeptIds] = useState<string[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);

  // Data — load once, share across reports
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  const [budgets, setBudgets] = useState<ProjectBudget[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [entries, setEntries] = useState<HourEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entriesError, setEntriesError] = useState<string | null>(null);

  // Initial load — projects/clients/people stay loaded; entries refresh per filter.
  // Promise.allSettled so one failing service doesn't blank out the others.
  const loadShared = React.useCallback(() => {
    (async () => {
      try {
        const results = await Promise.allSettled([
          TeamService.list(),
          DepartmentService.list(),
          ProjectService.list(),
          OrgService.list(),
          AdminService.billingRoles.list(),
          ProjectBudgetService.list(),
          InvoiceService.list(),
        ]);
        const [m, d, p, o, br, b, inv] = results;
        setMembers(m.status === 'fulfilled' ? m.value : []);
        setDepartments(d.status === 'fulfilled' ? d.value : []);
        setProjects(p.status === 'fulfilled' ? p.value : []);
        setOrgs(o.status === 'fulfilled' ? o.value : []);
        setBillingRoles(br.status === 'fulfilled' ? br.value : []);
        setBudgets(b.status === 'fulfilled' ? b.value : []);
        setInvoices(inv.status === 'fulfilled' ? inv.value : []);
      } catch (err: any) {
        toast('Load failed: ' + (err?.message || String(err)), true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => loadShared(), [loadShared]);

  // Reload entries when date range changes. `quiet` (a sync push) skips the
  // loading skeleton so live refreshes never blank the report.
  const loadEntries = React.useCallback(
    (quiet?: boolean) => {
      (async () => {
        if (!quiet) setLoading(true);
        setEntriesError(null);
        try {
          const data = await TimeService.listEntries(undefined, startDate, endDate);
          setEntries(data);
        } catch (err: any) {
          console.error('[Reports] entries load failed:', err);
          setEntriesError(
            'Could not load time entries for this range. ' +
              (err?.message || 'Try a different date range.')
          );
        }
        setLoading(false);
      })();
    },
    [startDate, endDate]
  );
  useEffect(() => loadEntries(), [loadEntries]);

  // Live: refresh when any report source changes (another window or user).
  useTableSync(
    [
      'team_members',
      'departments',
      'projects',
      'organizations',
      'billing_roles',
      'project_budgets',
      'invoices',
    ],
    loadShared
  );
  useTableSync(['hour_entries'], () => loadEntries(true));

  // Filtered datasets — applied inside each report's compute step
  const filteredMembers = useMemo(() => {
    let m = members.filter((x) => x.active);
    if (selectedDeptIds.length > 0) {
      m = m.filter((x) => x.departmentId && selectedDeptIds.includes(x.departmentId));
    }
    if (selectedPersonIds.length > 0) {
      m = m.filter((x) => selectedPersonIds.includes(x.id));
    }
    return m;
  }, [members, selectedDeptIds, selectedPersonIds]);

  const filteredProjects = useMemo(() => {
    if (selectedProjectIds.length === 0) return projects;
    return projects.filter((p) => selectedProjectIds.includes(p.id));
  }, [projects, selectedProjectIds]);

  const filteredEntries = useMemo(() => {
    const memberIds = new Set(filteredMembers.map((m) => m.id));
    const projectIds = new Set(filteredProjects.map((p) => p.id));
    return entries.filter(
      (e) => memberIds.has(e.teamMemberId) && (projectIds.size === 0 || projectIds.has(e.projectId))
    );
  }, [entries, filteredMembers, filteredProjects]);

  const reportMeta = REPORTS.find((r) => r.id === activeReport)!;
  const moneyHidden = reportMeta.needsFinancials && !canViewMoney;

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: 'var(--space-5)',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, margin: 0 }}>
          Reports & Analytics
        </h2>
        <p
          style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
            margin: 'var(--space-1) 0 0',
          }}
        >
          Run canonical reports across your firm. Filter, view, export.
        </p>
      </div>

      {/* Report tabs */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-1)',
          background: 'var(--sa)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-1)',
          marginBottom: 'var(--space-4)',
          overflowX: 'auto',
        }}
      >
        {availableReports.map((r) => (
          <button
            key={r.id}
            onClick={() => setActiveReport(r.id)}
            title={r.description}
            aria-pressed={activeReport === r.id}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeReport === r.id ? 'var(--sf)' : 'transparent',
              color: activeReport === r.id ? 'var(--tx)' : 'var(--tm)',
              fontSize: 'var(--font-size-base)',
              fontWeight: activeReport === r.id ? 700 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              boxShadow: activeReport === r.id ? 'var(--shadow-sm)' : 'none',
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-4)',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <label htmlFor="reports-start-date" style={filterLabel}>
            From
          </label>
          <input
            id="reports-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={dateInput}
          />
          <label htmlFor="reports-end-date" style={filterLabel}>
            to
          </label>
          <input
            id="reports-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={dateInput}
          />
        </div>

        <FilterDropdown
          label="Departments"
          options={departments.map((d) => ({
            id: d.id,
            label: d.abbreviation || d.name,
            color: d.color,
          }))}
          selected={selectedDeptIds}
          onChange={setSelectedDeptIds}
        />

        <FilterDropdown
          label="Projects"
          options={projects.map((p) => ({
            id: p.id,
            label: p.projectNumber ? `${p.projectNumber} — ${p.name}` : p.name,
          }))}
          selected={selectedProjectIds}
          onChange={setSelectedProjectIds}
        />

        <FilterDropdown
          label="People"
          options={members.filter((m) => m.active).map((m) => ({ id: m.id, label: m.name }))}
          selected={selectedPersonIds}
          onChange={setSelectedPersonIds}
        />

        <div style={{ flex: 1 }} />

        {(selectedDeptIds.length || selectedProjectIds.length || selectedPersonIds.length) > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedDeptIds([]);
              setSelectedProjectIds([]);
              setSelectedPersonIds([]);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Report body */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {entriesError && (
          <div
            role="alert"
            style={{
              padding: 'var(--space-3) var(--space-4)',
              marginBottom: 'var(--space-4)',
              background: 'color-mix(in srgb, var(--dg) 10%, transparent)',
              border: '1px solid var(--dg)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--dg)',
              fontSize: 'var(--font-size-base)',
            }}
          >
            {entriesError}
          </div>
        )}
        {moneyHidden ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-6)',
              textAlign: 'center',
              color: 'var(--tm)',
              fontSize: 'var(--font-size-base)',
              background: 'var(--sa)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <Icon icon={Lock} em={1.2308} aria-hidden="true" />
            <span>
              This report shows financial figures. You need the
              <strong> reports.view_financials </strong> permission to run it.
            </span>
          </div>
        ) : loading ? (
          <ReportSkeleton />
        ) : (
          <ReportBody
            reportId={activeReport}
            members={members}
            departments={departments}
            projects={projects}
            orgs={orgs}
            billingRoles={billingRoles}
            budgets={budgets}
            invoices={invoices}
            entries={filteredEntries}
            allEntries={entries}
            startDate={startDate}
            endDate={endDate}
            canViewMoney={canViewMoney}
            canExportCsv={canExportCsv}
            canExportPdf={canExportPdf}
            toast={toast}
          />
        )}
      </div>
    </div>
  );
}

// ─── Report bodies router ────────────────────────────────────────────

function ReportBody(props: any) {
  switch (props.reportId as ReportId) {
    case 'utilization':
      return <UtilizationReport {...props} />;
    case 'time_by_project':
      return <TimeByProjectReport {...props} />;
    case 'time_by_person':
      return <TimeByPersonReport {...props} />;
    case 'wip':
      return <WipReport {...props} />;
    case 'profitability':
      return <ProfitabilityReport {...props} />;
    case 'ar_aging':
      return <ARagingReport {...props} />;
    default:
      return null;
  }
}

// ─── Loading skeleton ────────────────────────────────────────────────
// Shown while entries reload for a new date range. Mirrors the report
// shell's layout (KPI strip + chart + table) so nothing jumps on arrival.

function ReportSkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading report data"
      style={{
        background: 'var(--sf)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-1)', background: 'var(--bd)' }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: rem(56),
              background: 'var(--sf)',
              opacity: Math.max(0.35, 0.9 - i * 0.15),
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: rem(200),
          background: 'var(--sa)',
          borderRadius: 'var(--radius-md)',
          animation: 'pulse 1.4s ease-in-out infinite',
        }}
      />
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: rem(32),
            background: 'var(--sa)',
            borderRadius: 'var(--radius-sm)',
            opacity: Math.max(0.3, 0.9 - i * 0.15),
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

// ─── Utilization ─────────────────────────────────────────────────────

function UtilizationReport({
  members,
  entries,
  startDate,
  endDate,
  canExportCsv,
  canExportPdf,
}: any) {
  const data = useMemo(() => {
    // Days in range (inclusive)
    const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000 + 1;
    const weeks = Math.max(1, days / 7);
    return members
      .map((m: TeamMember) => {
        const myEntries = entries.filter((e: HourEntry) => e.teamMemberId === m.id);
        const billable = myEntries
          .filter((e: any) => e.billable !== false)
          .reduce((s: number, e: HourEntry) => s + (Number(e.hours) || 0), 0);
        const nonBill =
          myEntries.reduce((s: number, e: HourEntry) => s + (Number(e.hours) || 0), 0) - billable;
        const cap =
          (m.weeklyCapacityHours ?? (m.employmentType === 'part_time_hourly' ? 20 : 40)) * weeks;
        const util = cap > 0 ? (billable / cap) * 100 : 0;
        return { name: m.name, billable, nonBillable: nonBill, capacity: cap, utilization: util };
      })
      .sort((a: any, b: any) => b.utilization - a.utilization);
  }, [members, entries, startDate, endDate]);

  const totals = useMemo(() => {
    const billable = data.reduce((s: number, r: any) => s + r.billable, 0);
    const cap = data.reduce((s: number, r: any) => s + r.capacity, 0);
    return { billable, capacity: cap, util: cap > 0 ? (billable / cap) * 100 : 0 };
  }, [data]);

  // No billable/non-billable hours at all → blank chart + all-red 0% table.
  // Show a calm empty-data state instead; util color stays neutral so the
  // summary doesn't shout red over a meaningless zero.
  const noData = hasNoHours(entries);

  return (
    <ReportShell
      title="Utilization"
      subtitle={`${startDate} to ${endDate}`}
      summary={[
        { label: 'TOTAL BILLABLE', value: fmtHours(totals.billable) },
        { label: 'COMBINED CAPACITY', value: fmtHours(totals.capacity) },
        {
          label: 'UTILIZATION',
          value: fmtPct(totals.util),
          color: noData ? undefined : utilColor(totals.util),
        },
      ]}
      onExportCsv={canExportCsv && !noData ? () => exportUtilCsv(data) : undefined}
      onExportPdf={
        canExportPdf && !noData ? () => exportUtilPdf(data, startDate, endDate) : undefined
      }
    >
      {noData ? (
        <EmptyState icon={NoTimeIcon} title={NO_TIME_TITLE} subtitle={NO_TIME_SUBTITLE} />
      ) : (
        <>
          <div style={{ height: rem(240), marginBottom: 'var(--space-4)' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.slice(0, 20)}
                margin={{ top: 10, right: 10, bottom: 30, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: 'var(--tm)' }}
                  angle={-30}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 10, fill: 'var(--tm)' }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="billable"
                  stackId="a"
                  fill="var(--ok)"
                  name="Billable"
                  radius={[3, 3, 0, 0]}
                />
                <Bar dataKey="nonBillable" stackId="a" fill="var(--tm)" name="Non-billable" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ReportTable
            columns={[
              { key: 'name', label: 'Person', width: '1fr' },
              { key: 'billable', label: 'Billable', width: 100, align: 'right', format: fmtHours },
              {
                key: 'nonBillable',
                label: 'Non-bill',
                width: 100,
                align: 'right',
                format: fmtHours,
              },
              { key: 'capacity', label: 'Capacity', width: 100, align: 'right', format: fmtHours },
              {
                key: 'utilization',
                label: 'Util %',
                width: 100,
                align: 'right',
                format: fmtPct,
                colorFn: (v) => utilColor(v),
              },
            ]}
            rows={data}
            totals={{
              name: 'Totals',
              billable: totals.billable,
              nonBillable: data.reduce((s: number, r: any) => s + r.nonBillable, 0),
              capacity: totals.capacity,
              utilization: totals.util,
            }}
          />
        </>
      )}
    </ReportShell>
  );
}

function utilColor(util: number): string {
  if (util >= 75) return 'var(--ok)';
  if (util >= 50) return 'var(--color-warning)';
  return 'var(--dg)';
}

// ─── Time by Project ─────────────────────────────────────────────────

function TimeByProjectReport({
  projects,
  entries,
  startDate,
  endDate,
  canExportCsv,
  canExportPdf,
}: any) {
  const data = useMemo(() => {
    const byProject = new Map<string, { hours: number; billable: number }>();
    for (const e of entries) {
      const cur = byProject.get(e.projectId) || { hours: 0, billable: 0 };
      cur.hours += Number(e.hours) || 0;
      if ((e as any).billable !== false) cur.billable += Number(e.hours) || 0;
      byProject.set(e.projectId, cur);
    }
    return Array.from(byProject.entries())
      .map(([projectId, d]) => {
        const p = projects.find((x: Project) => x.id === projectId);
        return {
          projectId,
          projectName: p ? `${p.projectNumber ? p.projectNumber + ' — ' : ''}${p.name}` : 'Unknown',
          hours: d.hours,
          billable: d.billable,
        };
      })
      .sort((a, b) => b.hours - a.hours);
  }, [entries, projects]);

  const total = data.reduce((s, r) => s + r.hours, 0);
  const billable = data.reduce((s, r) => s + r.billable, 0);
  const noData = hasNoHours(entries);

  return (
    <ReportShell
      title="Time by Project"
      subtitle={`${startDate} to ${endDate}`}
      summary={[
        { label: 'TOTAL HOURS', value: fmtHours(total) },
        { label: 'BILLABLE', value: fmtHours(billable) },
        { label: 'PROJECTS', value: String(data.length) },
      ]}
      onExportCsv={
        canExportCsv && !noData
          ? () =>
              exportGenericCsv(
                'time-by-project',
                ['Project', 'Hours', 'Billable'],
                data.map((r) => [r.projectName, r.hours, r.billable])
              )
          : undefined
      }
      onExportPdf={
        canExportPdf && !noData
          ? () =>
              exportGenericPdf(
                'Time by Project',
                `${startDate} to ${endDate}`,
                ['Project', 'Hours', 'Billable'],
                data.map((r) => [r.projectName, fmtHours(r.hours), fmtHours(r.billable)])
              )
          : undefined
      }
    >
      {noData ? (
        <EmptyState icon={NoTimeIcon} title={NO_TIME_TITLE} subtitle={NO_TIME_SUBTITLE} />
      ) : (
        <>
          <div style={{ height: rem(240), marginBottom: 'var(--space-4)' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.slice(0, 15)}
                layout="vertical"
                margin={{ top: 10, right: 20, bottom: 10, left: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--tm)' }} />
                <YAxis
                  type="category"
                  dataKey="projectName"
                  tick={{ fontSize: 10, fill: 'var(--tm)' }}
                  width={150}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="hours" fill="var(--ac)" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ReportTable
            columns={[
              { key: 'projectName', label: 'Project', width: '1fr' },
              { key: 'hours', label: 'Hours', width: 100, align: 'right', format: fmtHours },
              { key: 'billable', label: 'Billable', width: 100, align: 'right', format: fmtHours },
            ]}
            rows={data}
            totals={{ projectName: 'Totals', hours: total, billable }}
          />
        </>
      )}
    </ReportShell>
  );
}

// ─── Time by Person ──────────────────────────────────────────────────

function TimeByPersonReport({
  members,
  departments,
  entries,
  startDate,
  endDate,
  canExportCsv,
  canExportPdf,
}: any) {
  const data = useMemo(() => {
    const byMember = new Map<string, { hours: number; billable: number }>();
    for (const e of entries) {
      const cur = byMember.get(e.teamMemberId) || { hours: 0, billable: 0 };
      cur.hours += Number(e.hours) || 0;
      if ((e as any).billable !== false) cur.billable += Number(e.hours) || 0;
      byMember.set(e.teamMemberId, cur);
    }
    return Array.from(byMember.entries())
      .map(([memberId, d]) => {
        const m = members.find((x: TeamMember) => x.id === memberId);
        const dept = m?.departmentId
          ? departments.find((dd: Department) => dd.id === m.departmentId)
          : null;
        return {
          memberId,
          name: m?.name || 'Unknown',
          department: dept?.abbreviation || dept?.name || '—',
          hours: d.hours,
          billable: d.billable,
        };
      })
      .sort((a, b) => b.hours - a.hours);
  }, [entries, members, departments]);

  const total = data.reduce((s, r) => s + r.hours, 0);
  const noData = hasNoHours(entries);

  return (
    <ReportShell
      title="Time by Person"
      subtitle={`${startDate} to ${endDate}`}
      summary={[
        { label: 'TOTAL HOURS', value: fmtHours(total) },
        { label: 'PEOPLE LOGGED', value: String(data.length) },
      ]}
      onExportCsv={
        canExportCsv && !noData
          ? () =>
              exportGenericCsv(
                'time-by-person',
                ['Person', 'Department', 'Hours', 'Billable'],
                data.map((r) => [r.name, r.department, r.hours, r.billable])
              )
          : undefined
      }
      onExportPdf={
        canExportPdf && !noData
          ? () =>
              exportGenericPdf(
                'Time by Person',
                `${startDate} to ${endDate}`,
                ['Person', 'Department', 'Hours', 'Billable'],
                data.map((r) => [r.name, r.department, fmtHours(r.hours), fmtHours(r.billable)])
              )
          : undefined
      }
    >
      {noData ? (
        <EmptyState icon={NoTimeIcon} title={NO_TIME_TITLE} subtitle={NO_TIME_SUBTITLE} />
      ) : (
        <ReportTable
          columns={[
            { key: 'name', label: 'Person', width: '1fr' },
            { key: 'department', label: 'Department', width: 130 },
            { key: 'hours', label: 'Hours', width: 100, align: 'right', format: fmtHours },
            { key: 'billable', label: 'Billable', width: 100, align: 'right', format: fmtHours },
          ]}
          rows={data}
          totals={{
            name: 'Totals',
            department: '',
            hours: total,
            billable: data.reduce((s, r) => s + r.billable, 0),
          }}
        />
      )}
    </ReportShell>
  );
}

// ─── WIP ─────────────────────────────────────────────────────────────

function WipReport({ projects, entries, members, billingRoles, canExportCsv, canExportPdf }: any) {
  const data = useMemo(() => {
    // Billable, non-invoiced entries
    const wipEntries = entries.filter((e: any) => e.billable !== false && !e.invoiceId);
    const byProject = new Map<string, { hours: number; value: number }>();
    for (const e of wipEntries) {
      const m = members.find((mm: TeamMember) => mm.id === e.teamMemberId);
      const role = m ? billingRoles.find((r: BillingRole) => r.id === m.billingRoleId) : null;
      const rate = m?.hourlyRate ?? role?.defaultRate ?? 0;
      const cur = byProject.get(e.projectId) || { hours: 0, value: 0 };
      cur.hours += Number(e.hours) || 0;
      cur.value += (Number(e.hours) || 0) * rate;
      byProject.set(e.projectId, cur);
    }
    return Array.from(byProject.entries())
      .map(([projectId, d]) => {
        const p = projects.find((x: Project) => x.id === projectId);
        return {
          projectName: p ? `${p.projectNumber ? p.projectNumber + ' — ' : ''}${p.name}` : 'Unknown',
          hours: d.hours,
          value: d.value,
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [entries, projects, members, billingRoles]);

  const totalHours = data.reduce((s, r) => s + r.hours, 0);
  const totalValue = data.reduce((s, r) => s + r.value, 0);

  return (
    <ReportShell
      title="Work-In-Progress (WIP)"
      subtitle="Billable hours logged but not yet invoiced"
      summary={[
        { label: 'WIP HOURS', value: fmtHours(totalHours) },
        { label: 'WIP VALUE', value: fmtMoney(totalValue), color: 'var(--ac)' },
        { label: 'PROJECTS', value: String(data.length) },
      ]}
      onExportCsv={
        canExportCsv
          ? () =>
              exportGenericCsv(
                'wip',
                ['Project', 'Hours', 'Value'],
                data.map((r) => [r.projectName, r.hours, r.value])
              )
          : undefined
      }
      onExportPdf={
        canExportPdf
          ? () =>
              exportGenericPdf(
                'WIP Report',
                'Billable hours not yet invoiced',
                ['Project', 'Hours', 'Value'],
                data.map((r) => [r.projectName, fmtHours(r.hours), fmtMoney(r.value)])
              )
          : undefined
      }
    >
      <ReportTable
        columns={[
          { key: 'projectName', label: 'Project', width: '1fr' },
          { key: 'hours', label: 'WIP Hours', width: 110, align: 'right', format: fmtHours },
          { key: 'value', label: 'WIP $', width: 120, align: 'right', format: fmtMoney },
        ]}
        rows={data}
        totals={{ projectName: 'Totals', hours: totalHours, value: totalValue }}
      />
    </ReportShell>
  );
}

// ─── Profitability ───────────────────────────────────────────────────

function ProfitabilityReport({
  projects,
  budgets,
  entries,
  members,
  billingRoles,
  canExportCsv,
  canExportPdf,
}: any) {
  const data = useMemo(() => {
    return projects
      .map((p: Project) => {
        const projectBudgets = budgets.filter((b: ProjectBudget) => b.projectId === p.id);
        const totalBudget = projectBudgets.find(
          (b: ProjectBudget) => !b.phaseId && !b.billingRoleId
        );
        const budgetDollars =
          totalBudget?.budgetAmount ?? ProjectBudgetService.computeRollup(projectBudgets).amount;

        const projectEntries = entries.filter((e: HourEntry) => e.projectId === p.id);
        let actualCost = 0;
        let actualHours = 0;
        for (const e of projectEntries) {
          actualHours += Number(e.hours) || 0;
          const m = members.find((mm: TeamMember) => mm.id === e.teamMemberId);
          const role = m ? billingRoles.find((r: BillingRole) => r.id === m.billingRoleId) : null;
          const rate = m?.hourlyRate ?? role?.defaultRate ?? 0;
          actualCost += (Number(e.hours) || 0) * rate;
        }

        const variance = budgetDollars - actualCost;
        const margin = budgetDollars > 0 ? (variance / budgetDollars) * 100 : 0;

        return {
          projectName: p.projectNumber ? `${p.projectNumber} — ${p.name}` : p.name,
          budget: budgetDollars,
          actual: actualCost,
          actualHours,
          variance,
          margin,
        };
      })
      .filter((r: any) => r.budget > 0 || r.actual > 0)
      .sort((a: any, b: any) => b.actual - a.actual);
  }, [projects, budgets, entries, members, billingRoles]);

  const totals = data.reduce(
    (acc: any, r: any) => ({ budget: acc.budget + r.budget, actual: acc.actual + r.actual }),
    { budget: 0, actual: 0 }
  );
  const totalVariance = totals.budget - totals.actual;

  return (
    <ReportShell
      title="Project Profitability"
      subtitle="Budget vs actual cost"
      summary={[
        { label: 'TOTAL BUDGET', value: fmtMoney(totals.budget) },
        { label: 'TOTAL ACTUAL', value: fmtMoney(totals.actual) },
        {
          label: 'NET VARIANCE',
          value: fmtMoney(totalVariance),
          color: totalVariance >= 0 ? 'var(--ok)' : 'var(--dg)',
        },
      ]}
      onExportCsv={
        canExportCsv
          ? () =>
              exportGenericCsv(
                'profitability',
                ['Project', 'Budget', 'Actual', 'Variance', 'Margin %'],
                data.map((r: any) => [r.projectName, r.budget, r.actual, r.variance, r.margin])
              )
          : undefined
      }
      onExportPdf={
        canExportPdf
          ? () =>
              exportGenericPdf(
                'Project Profitability',
                'Budget vs actual cost',
                ['Project', 'Budget', 'Actual', 'Variance', 'Margin %'],
                data.map((r: any) => [
                  r.projectName,
                  fmtMoney(r.budget),
                  fmtMoney(r.actual),
                  fmtMoney(r.variance),
                  fmtPct(r.margin),
                ])
              )
          : undefined
      }
    >
      <ReportTable
        columns={[
          { key: 'projectName', label: 'Project', width: '1fr' },
          { key: 'budget', label: 'Budget', width: 110, align: 'right', format: fmtMoney },
          { key: 'actual', label: 'Actual', width: 110, align: 'right', format: fmtMoney },
          {
            key: 'variance',
            label: 'Variance',
            width: 110,
            align: 'right',
            format: fmtMoney,
            colorFn: (v) => (v >= 0 ? 'var(--ok)' : 'var(--dg)'),
          },
          {
            key: 'margin',
            label: 'Margin %',
            width: 90,
            align: 'right',
            format: fmtPct,
            colorFn: (v) => (v >= 0 ? 'var(--ok)' : 'var(--dg)'),
          },
        ]}
        rows={data}
        totals={{
          projectName: 'Totals',
          budget: totals.budget,
          actual: totals.actual,
          variance: totalVariance,
          margin: totals.budget > 0 ? (totalVariance / totals.budget) * 100 : 0,
        }}
      />
    </ReportShell>
  );
}

// ─── AR Aging ────────────────────────────────────────────────────────

function ARagingReport({ invoices, orgs, projects, canExportCsv, canExportPdf }: any) {
  const buckets = useMemo(() => InvoiceService.ageingBuckets(invoices), [invoices]);
  const total = buckets.current + buckets.d30 + buckets.d60 + buckets.d90;

  const detail = useMemo(() => {
    const today = new Date();
    return invoices
      .filter((i: Invoice) => i.status !== 'paid' && i.status !== 'void' && i.status !== 'draft')
      .map((i: Invoice) => {
        const issued = (i as any).issueDate || i.date;
        const days = issued
          ? Math.floor((today.getTime() - new Date(issued).getTime()) / 86400000)
          : 0;
        const open = (i.total || 0) - ((i as any).amountPaid || 0);
        let bucket = 'Current';
        if (days > 90) bucket = '90+';
        else if (days > 60) bucket = '60+';
        else if (days > 30) bucket = '30+';
        const project = projects.find((p: Project) => p.id === i.projectId);
        const org = orgs.find((o: Organization) => o.id === i.organizationId);
        return {
          invoiceNumber: i.invoiceNumber,
          client: org?.name || 'Unknown',
          project: project?.name || 'Unknown',
          issued: issued || '—',
          due: i.dueDate || '—',
          days,
          bucket,
          open,
        };
      })
      .filter((r: any) => r.open > 0)
      .sort((a: any, b: any) => b.days - a.days);
  }, [invoices, orgs, projects]);

  // Aging-severity palette (current→green, 30+→amber, 60+→orange, 90+→red)
  // matches the same convention used across dashboard/forecasting widgets —
  // a deliberate, cross-app data-severity encoding, not a one-off color choice.
  const pieData = [
    { name: 'Current', value: buckets.current, color: 'var(--ok)' },
    { name: '30+', value: buckets.d30, color: 'var(--color-warning)' },
    { name: '60+', value: buckets.d60, color: 'var(--color-caution)' },
    { name: '90+', value: buckets.d90, color: 'var(--dg)' },
  ].filter((d) => d.value > 0);

  return (
    <ReportShell
      title="AR Aging"
      subtitle="Outstanding receivables"
      summary={[
        { label: 'TOTAL OPEN', value: fmtMoney(total), color: 'var(--ac)' },
        { label: '60+ DAYS', value: fmtMoney(buckets.d60 + buckets.d90), color: 'var(--dg)' },
        { label: 'INVOICES', value: String(detail.length) },
      ]}
      onExportCsv={
        canExportCsv
          ? () =>
              exportGenericCsv(
                'ar-aging',
                ['Invoice #', 'Client', 'Project', 'Issued', 'Due', 'Days', 'Bucket', 'Open $'],
                detail.map((r: any) => [
                  r.invoiceNumber,
                  r.client,
                  r.project,
                  r.issued,
                  r.due,
                  r.days,
                  r.bucket,
                  r.open,
                ])
              )
          : undefined
      }
      onExportPdf={
        canExportPdf
          ? () =>
              exportGenericPdf(
                'AR Aging',
                'Outstanding receivables',
                ['Invoice #', 'Client', 'Project', 'Days', 'Bucket', 'Open'],
                detail.map((r: any) => [
                  r.invoiceNumber,
                  r.client,
                  r.project,
                  r.days,
                  r.bucket,
                  fmtMoney(r.open),
                ])
              )
          : undefined
      }
    >
      {pieData.length > 0 && (
        <div style={{ height: rem(220), marginBottom: 'var(--space-4)' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={(e) => `${e.name}: ${fmtMoney(e.value)}`}
              >
                {pieData.map((d) => (
                  <Cell key={d.name} fill={d.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => fmtMoney(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <ReportTable
        columns={[
          { key: 'invoiceNumber', label: 'Invoice #', width: 120 },
          { key: 'client', label: 'Client', width: '1fr' },
          { key: 'project', label: 'Project', width: '1fr' },
          { key: 'days', label: 'Days', width: 70, align: 'right' },
          { key: 'bucket', label: 'Bucket', width: 90 },
          { key: 'open', label: 'Open $', width: 110, align: 'right', format: fmtMoney },
        ]}
        rows={detail}
        totals={{
          invoiceNumber: 'Totals',
          client: '',
          project: '',
          days: '',
          bucket: '',
          open: total,
        }}
      />
    </ReportShell>
  );
}

// ─── Shared shell ────────────────────────────────────────────────────

function ReportShell({
  title,
  subtitle,
  summary,
  onExportCsv,
  onExportPdf,
  children,
}: {
  title: string;
  subtitle: string;
  summary: { label: string; value: string; color?: string }[];
  onExportCsv?: () => void;
  onExportPdf?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: 'var(--sf)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          padding: 'var(--space-4)',
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <div>
          <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}>{title}</h3>
          <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)', marginTop: rem(2) }}>
            {subtitle}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {onExportCsv && (
            <Button variant="outline" size="sm" onClick={onExportCsv}>
              Export CSV
            </Button>
          )}
          {onExportPdf && (
            <Button variant="outline" size="sm" onClick={onExportPdf}>
              Export PDF
            </Button>
          )}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${summary.length}, 1fr)`,
          gap: rem(1),
          background: 'var(--bd)',
        }}
      >
        {summary.map((s) => (
          <div key={s.label} style={{ background: 'var(--sf)', padding: 'var(--space-4)' }}>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                color: 'var(--tm)',
                letterSpacing: '.05em',
              }}
            >
              {s.label}
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                color: s.color || 'var(--tx)',
                fontFamily: 'var(--font-mono)',
                marginTop: 'var(--space-1)',
              }}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>
      <div style={{ padding: 'var(--space-4)' }}>{children}</div>
    </div>
  );
}

// ─── Generic table ───────────────────────────────────────────────────

interface TableCol {
  key: string;
  label: string;
  width: number | string;
  align?: 'left' | 'right' | 'center';
  format?: (v: any) => string;
  colorFn?: (v: any) => string;
}

function ReportTable({
  columns,
  rows,
  totals,
}: {
  columns: TableCol[];
  rows: any[];
  totals?: any;
}) {
  const tmpl = columns
    .map((c) => (typeof c.width === 'number' ? `${c.width}px` : c.width))
    .join(' ');
  return (
    <div
      style={{
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: tmpl,
          gap: 0,
          background: 'var(--sa)',
          padding: 'var(--space-2) var(--space-3)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--tm)',
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        {columns.map((c) => (
          <span key={c.key} style={{ textAlign: c.align || 'left' }}>
            {c.label}
          </span>
        ))}
      </div>
      <div style={{ maxHeight: rem(480), overflow: 'auto' }}>
        {rows.map((r, i) => (
          <div
            key={i}
            style={{
              display: 'grid',
              gridTemplateColumns: tmpl,
              padding: 'var(--space-2) var(--space-3)',
              borderTop: '1px solid var(--bd)',
              fontSize: 'var(--font-size-base)',
              alignItems: 'center',
            }}
          >
            {columns.map((c) => {
              const v = r[c.key];
              const display = c.format ? c.format(v) : String(v ?? '');
              return (
                <span
                  key={c.key}
                  style={{
                    textAlign: c.align || 'left',
                    fontFamily: c.align === 'right' ? 'var(--font-mono)' : 'inherit',
                    color: c.colorFn ? c.colorFn(v) : 'var(--tx)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {display}
                </span>
              );
            })}
          </div>
        ))}
        {rows.length === 0 && <EmptyState title="No rows match the current filters" />}
      </div>
      {totals && rows.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: tmpl,
            padding: 'var(--space-2) var(--space-3)',
            background: 'var(--sa)',
            borderTop: '2px solid var(--bd)',
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
          }}
        >
          {columns.map((c) => {
            const v = totals[c.key];
            const display = v === undefined || v === '' ? '' : c.format ? c.format(v) : String(v);
            return (
              <span
                key={c.key}
                style={{
                  textAlign: c.align || 'left',
                  fontFamily: c.align === 'right' ? 'var(--font-mono)' : 'inherit',
                  color: 'var(--tx)',
                }}
              >
                {display}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Filter dropdown ─────────────────────────────────────────────────

function FilterDropdown({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { id: string; label: string; color?: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        style={{
          background:
            selected.length > 0 ? 'color-mix(in srgb, var(--ac) 12%, transparent)' : undefined,
          color: selected.length > 0 ? 'var(--ac)' : undefined,
          fontWeight: selected.length > 0 ? 700 : 500,
        }}
      >
        {label}
        {selected.length > 0 && (
          <span
            style={{
              background: 'var(--ac)',
              color: 'var(--color-text-inverse)',
              borderRadius: 'var(--radius-full)',
              fontSize: 'var(--font-size-xs)',
              padding: `${rem(2)} var(--space-2)`,
            }}
          >
            {selected.length}
          </span>
        )}
        <Icon icon={ChevronDown} em={1.2} aria-hidden="true" />
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-1))',
            left: 0,
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 20,
            minWidth: rem(240),
            maxHeight: rem(320),
            overflow: 'auto',
            padding: 'var(--space-2)',
          }}
        >
          {options.length === 0 && (
            <div
              style={{
                padding: 'var(--space-2)',
                color: 'var(--tm)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              No options
            </div>
          )}
          {options.map((o) => {
            const active = selected.includes(o.id);
            return (
              <div
                key={o.id}
                onClick={() => {
                  const next = active ? selected.filter((x) => x !== o.id) : [...selected, o.id];
                  onChange(next);
                }}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--tx)',
                  background: active ? 'color-mix(in srgb, var(--ac) 10%, transparent)' : '',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = active
                    ? 'color-mix(in srgb, var(--ac) 10%, transparent)'
                    : '')
                }
              >
                <div
                  style={{
                    width: rem(14),
                    height: rem(14),
                    border: `2px solid ${active ? 'var(--ac)' : 'var(--bd)'}`,
                    background: active ? 'var(--ac)' : 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-text-inverse)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {active ? <Icon icon={Check} em={0.8462} aria-hidden="true" /> : null}
                </div>
                {o.color && (
                  <span
                    style={{
                      width: rem(10),
                      height: rem(10),
                      borderRadius: 'var(--radius-sm)',
                      background: o.color,
                    }}
                  />
                )}
                <span style={{ flex: 1 }}>{o.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Export helpers ──────────────────────────────────────────────────

function exportGenericCsv(name: string, headers: string[], rows: (string | number)[][]) {
  const escape = (v: any) => {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const lines = [headers.join(','), ...rows.map((r) => r.map(escape).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}-${todayIso()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportGenericPdf(title: string, subtitle: string, headers: string[], rows: any[][]) {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'landscape' });
  pdf.setFontSize(18);
  pdf.text(title, 40, 50);
  pdf.setFontSize(10);
  pdf.setTextColor(120);
  pdf.text(subtitle, 40, 68);

  autoTable(pdf, {
    startY: 90,
    head: [headers],
    body: rows.map((r) => r.map((c) => String(c))),
    headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9, cellPadding: 4 },
  });
  pdf.save(`${title.toLowerCase().replace(/\s+/g, '-')}-${todayIso()}.pdf`);
}

function exportUtilCsv(rows: any[]) {
  exportGenericCsv(
    'utilization',
    ['Person', 'Billable Hours', 'Non-billable Hours', 'Capacity', 'Utilization %'],
    rows.map((r) => [r.name, r.billable, r.nonBillable, r.capacity, r.utilization])
  );
}

function exportUtilPdf(rows: any[], startDate: string, endDate: string) {
  exportGenericPdf(
    'Utilization Report',
    `${startDate} to ${endDate}`,
    ['Person', 'Billable', 'Non-bill', 'Capacity', 'Util %'],
    rows.map((r) => [
      r.name,
      fmtHours(r.billable),
      fmtHours(r.nonBillable),
      fmtHours(r.capacity),
      fmtPct(r.utilization),
    ])
  );
}

// ─── Style constants ─────────────────────────────────────────────────

const tooltipStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-base)',
};

const filterLabel: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--tm)',
};

const dateInput: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  fontFamily: 'var(--font-mono)',
};
